/**
 * WB4 Production Interaction Hardening — Phase 3: Palette AI Intent Layer (SSOT).
 *
 * NL → PaletteOperationalIntent → candidate actionId + required payload.
 * Rule-based stub today; LLM classification extension point via assistive-llm-adapter.
 */

import type { WorkbenchRiskTier } from './workbench-action-safety-policy';

/** Five structured palette operational intents (Phase 3 SSOT). */
export type PaletteOperationalIntent =
  | 'customer_follow_up_task_create'
  | 'order_picking_production_handoff'
  | 'invoice_payment_status_explain'
  | 'blocked_action_reason_explain'
  | 'current_object_next_action_recommend';

export const PALETTE_OPERATIONAL_INTENTS = [
  'customer_follow_up_task_create',
  'order_picking_production_handoff',
  'invoice_payment_status_explain',
  'blocked_action_reason_explain',
  'current_object_next_action_recommend',
] as const satisfies readonly PaletteOperationalIntent[];

export interface PaletteIntentRequiredField {
  key: string;
  label: string;
}

export interface PaletteIntentSpec {
  intent: PaletteOperationalIntent;
  label: string;
  description: string;
  actionId: string;
  riskTier: WorkbenchRiskTier;
  requiredFields: readonly PaletteIntentRequiredField[];
  preferredDomains?: readonly string[];
}

export const PALETTE_INTENT_ACTION_ID_BY_INTENT: Readonly<
  Record<PaletteOperationalIntent, string>
> = {
  customer_follow_up_task_create: 'start_assignment_workflow',
  order_picking_production_handoff: 'start_production_workflow',
  invoice_payment_status_explain: 'view-related-records',
  blocked_action_reason_explain: 'view_audit',
  current_object_next_action_recommend: 'assignment-recommend',
};

export const PALETTE_INTENT_REQUIRED_FIELDS: Readonly<
  Record<PaletteOperationalIntent, readonly PaletteIntentRequiredField[]>
> = {
  customer_follow_up_task_create: [
    { key: 'customerId', label: '고객 ID' },
    { key: 'followUpSummary', label: '후속 작업 요약' },
  ],
  order_picking_production_handoff: [{ key: 'orderId', label: '주문 ID' }],
  invoice_payment_status_explain: [{ key: 'invoiceId', label: '청구/인보이스 ID' }],
  blocked_action_reason_explain: [{ key: 'blockedActionId', label: '차단된 액션 ID' }],
  current_object_next_action_recommend: [{ key: 'objectId', label: '대상 객체 ID' }],
};

export const PALETTE_OPERATIONAL_INTENT_SPECS: Readonly<
  Record<PaletteOperationalIntent, PaletteIntentSpec>
> = {
  customer_follow_up_task_create: {
    intent: 'customer_follow_up_task_create',
    label: '고객 후속 작업 생성',
    description: '고객 대상 후속 태스크/배정 워크플로를 시작합니다.',
    actionId: PALETTE_INTENT_ACTION_ID_BY_INTENT.customer_follow_up_task_create,
    riskTier: 'medium',
    requiredFields: PALETTE_INTENT_REQUIRED_FIELDS.customer_follow_up_task_create,
    preferredDomains: ['customer', 'counterparty', 'task_execution'],
  },
  order_picking_production_handoff: {
    intent: 'order_picking_production_handoff',
    label: '주문 피킹/생산 핸드오프',
    description: '주문을 피킹 큐 또는 생산 워크플로로 넘깁니다.',
    actionId: PALETTE_INTENT_ACTION_ID_BY_INTENT.order_picking_production_handoff,
    riskTier: 'high',
    requiredFields: PALETTE_INTENT_REQUIRED_FIELDS.order_picking_production_handoff,
    preferredDomains: ['order', 'sales_order', 'production_order'],
  },
  invoice_payment_status_explain: {
    intent: 'invoice_payment_status_explain',
    label: '청구/결제 상태 설명',
    description: '인보이스·결제 상태를 설명합니다 (실행 없음).',
    actionId: PALETTE_INTENT_ACTION_ID_BY_INTENT.invoice_payment_status_explain,
    riskTier: 'low',
    requiredFields: PALETTE_INTENT_REQUIRED_FIELDS.invoice_payment_status_explain,
    preferredDomains: ['collection', 'ar_collection', 'finance_state'],
  },
  blocked_action_reason_explain: {
    intent: 'blocked_action_reason_explain',
    label: '차단 액션 사유 설명',
    description: '실행이 차단된 액션의 사유를 설명합니다 (실행 없음).',
    actionId: PALETTE_INTENT_ACTION_ID_BY_INTENT.blocked_action_reason_explain,
    riskTier: 'low',
    requiredFields: PALETTE_INTENT_REQUIRED_FIELDS.blocked_action_reason_explain,
  },
  current_object_next_action_recommend: {
    intent: 'current_object_next_action_recommend',
    label: '다음 액션 추천',
    description: '현재 객체에 대한 다음 권장 액션을 제안합니다.',
    actionId: PALETTE_INTENT_ACTION_ID_BY_INTENT.current_object_next_action_recommend,
    riskTier: 'low',
    requiredFields: PALETTE_INTENT_REQUIRED_FIELDS.current_object_next_action_recommend,
  },
};

export type PaletteIntentResolutionSource = 'rule_match' | 'llm_classified';

export interface PaletteIntentResolution {
  intent: PaletteOperationalIntent;
  spec: PaletteIntentSpec;
  source: PaletteIntentResolutionSource;
  interpretationSummary: string;
  payload: Partial<Record<string, string>>;
  missingFieldKeys: readonly string[];
}

export interface ResolvePaletteIntentInput {
  query: string;
  businessDomain?: string;
  objectId?: string;
  customerId?: string;
  orderId?: string;
  invoiceId?: string;
  blockedActionId?: string;
  followUpSummary?: string;
  /** User-supplied clarification field values (Phase 5 increment). */
  clarification?: Partial<Record<string, string>>;
}

interface IntentPhraseRule {
  intent: PaletteOperationalIntent;
  patterns: readonly RegExp[];
}

const INTENT_PHRASE_RULES: readonly IntentPhraseRule[] = [
  {
    intent: 'customer_follow_up_task_create',
    patterns: [
      /고객.*후속.*(작업|태스크|업무).*(만들|생성|등록|추가)/i,
      /(follow[\s-]?up|followup).*(task|work).*(customer|client)/i,
      /create.*follow[\s-]?up.*task.*customer/i,
    ],
  },
  {
    intent: 'order_picking_production_handoff',
    patterns: [
      /주문.*(피킹|생산).*(넘기|넘겨|전달|핸드오프|이동|보내)/i,
      /order.*(picking|production).*(hand[\s-]?off|transfer|route)/i,
      /hand[\s-]?off.*order.*(picking|production)/i,
    ],
  },
  {
    intent: 'invoice_payment_status_explain',
    patterns: [
      /(청구|인보이스|송장).*(결제|납부|지급).*(상태|설명|알려|보여)/i,
      /explain.*(invoice|payment).*(status|state)/i,
      /(invoice|payment).*status.*explain/i,
    ],
  },
  {
    intent: 'blocked_action_reason_explain',
    patterns: [
      /(차단|막힌|실행.*안|disabled|blocked).*(이유|원인|설명|왜)/i,
      /why.*(blocked|disabled|can.?t|cannot).*(action|execute)/i,
      /explain.*blocked.*action/i,
    ],
  },
  {
    intent: 'current_object_next_action_recommend',
    patterns: [
      /(다음|추천).*(액션|작업|조치|스텝)/i,
      /recommend.*next.*action/i,
      /what.*(should|can).*do.*next/i,
      /next.*action.*(recommend|suggest)/i,
    ],
  },
];

function normalizeQuery(query: string): string {
  return query.trim().replace(/\s+/g, ' ');
}

function matchIntentFromQuery(query: string): PaletteOperationalIntent | undefined {
  const normalized = normalizeQuery(query);
  if (!normalized) {
    return undefined;
  }
  for (const rule of INTENT_PHRASE_RULES) {
    if (rule.patterns.some((pattern) => pattern.test(normalized))) {
      return rule.intent;
    }
  }
  return undefined;
}

function resolveOrderHandoffActionId(query: string): string {
  const normalized = normalizeQuery(query);
  if (/피킹|picking/i.test(normalized)) {
    return 'drag_drop:route_to_picking_queue';
  }
  return PALETTE_INTENT_ACTION_ID_BY_INTENT.order_picking_production_handoff;
}

function buildPayloadForIntent(
  intent: PaletteOperationalIntent,
  input: ResolvePaletteIntentInput
): Partial<Record<string, string>> {
  const payload: Partial<Record<string, string>> = {};

  if (input.customerId?.trim()) {
    payload.customerId = input.customerId.trim();
  }
  if (input.orderId?.trim()) {
    payload.orderId = input.orderId.trim();
  }
  if (input.invoiceId?.trim()) {
    payload.invoiceId = input.invoiceId.trim();
  }
  if (input.blockedActionId?.trim()) {
    payload.blockedActionId = input.blockedActionId.trim();
  }
  if (input.objectId?.trim()) {
    payload.objectId = input.objectId.trim();
  }
  if (input.followUpSummary?.trim()) {
    payload.followUpSummary = input.followUpSummary.trim();
  }

  if (input.clarification) {
    for (const [key, value] of Object.entries(input.clarification)) {
      if (value?.trim()) {
        payload[key] = value.trim();
      }
    }
  }

  if (intent === 'invoice_payment_status_explain' && !payload.invoiceId && payload.objectId) {
    payload.invoiceId = payload.objectId;
  }

  if (intent === 'current_object_next_action_recommend' && !payload.objectId && input.objectId) {
    payload.objectId = input.objectId.trim();
  }

  if (intent === 'customer_follow_up_task_create' && !payload.customerId && payload.objectId) {
    payload.customerId = payload.objectId;
  }

  return payload;
}

function resolveMissingFieldKeys(
  intent: PaletteOperationalIntent,
  payload: Partial<Record<string, string>>
): string[] {
  const required = PALETTE_INTENT_REQUIRED_FIELDS[intent];
  return required
    .filter((field) => {
      const value = payload[field.key]?.trim();
      return !value;
    })
    .map((field) => field.key);
}

function buildInterpretationSummary(
  spec: PaletteIntentSpec,
  missingFieldKeys: readonly string[]
): string {
  if (missingFieldKeys.length === 0) {
    return `${spec.label} — 실행 승인 대기 중`;
  }
  const missingLabels = missingFieldKeys
    .map((key) => spec.requiredFields.find((field) => field.key === key)?.label ?? key)
    .join(', ');
  return `${spec.label} — 추가 정보 필요: ${missingLabels}`;
}

/**
 * Rule-based NL → intent resolver (stub).
 * Extension point: assistive-llm-adapter may classify intents with source `llm_classified`.
 */
export function resolvePaletteIntentFromNaturalLanguage(
  input: ResolvePaletteIntentInput
): PaletteIntentResolution | undefined {
  const matchedIntent = matchIntentFromQuery(input.query);
  if (!matchedIntent) {
    return undefined;
  }

  const baseSpec = PALETTE_OPERATIONAL_INTENT_SPECS[matchedIntent];
  const spec: PaletteIntentSpec =
    matchedIntent === 'order_picking_production_handoff'
      ? {
          ...baseSpec,
          actionId: resolveOrderHandoffActionId(input.query),
        }
      : baseSpec;

  const payload = buildPayloadForIntent(matchedIntent, input);
  const missingFieldKeys = resolveMissingFieldKeys(matchedIntent, payload);

  return {
    intent: matchedIntent,
    spec,
    source: 'rule_match',
    interpretationSummary: buildInterpretationSummary(spec, missingFieldKeys),
    payload,
    missingFieldKeys,
  };
}

export function resolvePaletteIntentActionId(intent: PaletteOperationalIntent): string {
  return PALETTE_INTENT_ACTION_ID_BY_INTENT[intent];
}

export function isPaletteOperationalIntent(value: string): value is PaletteOperationalIntent {
  return (PALETTE_OPERATIONAL_INTENTS as readonly string[]).includes(value);
}
